import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Hide,
  Show,
  useColorModeValue,
  VStack,
  DividerProps,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';

import { useSideBarContext } from '../hooks/sideBarContext';
import { LogOutButton } from './LogOutButton';
import { SideBarHeader } from './SideBarHeader';
import { SideBarItem } from './SideBarItem';
import { colors } from 'packages/common-frontend/colors';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';
import { ExpandAndCollapseButton } from 'packages/sidebar/ui/ExpandAndCollapseButton';
import { useUserAuthUnsafe } from '~/contexts/UserAuthProvider';

function iterateOverChildren(childComponents: React.ReactNode) {
  return React.Children.map(childComponents, (child) => {
    if (!React.isValidElement(child) || child.type === Divider) {
      return child;
    }
    if (child.type === SideBarItem || child.type === SideBarHeader) {
      return React.cloneElement(child, {
        ...child.props,
        iconOnly: true,
      });
    }
    return null;
  });
}

type CollapsableSideBarProps = {
  children: React.ReactNode;
  dividerStyleProps?: DividerProps;
  additionalHeaderHeight?: number;
};
export const CollapsableSideBar = ({
  children,
  dividerStyleProps,
  additionalHeaderHeight = 0,
}: CollapsableSideBarProps) => {
  const { sideBarOpen, toggleSideBar } = useSideBarContext();
  const { logout } = useUserAuthUnsafe();

  const headerSpace = HEADER_HEIGHT + additionalHeaderHeight;

  const background = useColorModeValue('millet.100', '');
  const borderRight = useColorModeValue(
    `1px solid ${colors.gray['200']}`,
    'none',
  );
  const boxShadow = useColorModeValue('0 0 4px hsl(0deg 0% 76% / 35%)', 'none');
  const selectedColor = useColorModeValue('gray.300', 'gray.100');
  const toolTipBgColor = useColorModeValue('black', 'white');
  const toolTipColor = useColorModeValue('white', 'black');

  // Admin Dash refreshes pretty much on every click, so we cannot store state in a react hook.
  // To avoid "resetting" to a collapsed sidebar on every refresh, we can store state in sessionStorage,
  // which "lasts as long as the tab or the browser is open, and survives over page reloads and restores."
  // It also expires on page close (unlike localStorage) & new tabs are considered a new session.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(
    sessionStorage.getItem('isCollapsed')
      ? sessionStorage.getItem('isCollapsed') === 'true'
      : true,
  );

  const collapsedProps = {
    w: '75px',
  };

  const expandedProps = {
    w: '272px',
  };

  return (
    <>
      <Hide above="lg">
        <Drawer
          isOpen={sideBarOpen}
          size="full"
          isFullHeight
          placement="top"
          onClose={() => {
            toggleSideBar();
          }}
        >
          <DrawerOverlay>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerBody marginTop="75px">
                <VStack align="left">
                  {children}
                  <LogOutButton />
                </VStack>
              </DrawerBody>
            </DrawerContent>
          </DrawerOverlay>
        </Drawer>
      </Hide>

      <Show above="lg">
        <Flex
          id="collapsable-side-bar-container"
          direction="column"
          justify="space-between"
          bg={background}
          borderRight={borderRight}
          boxShadow={boxShadow}
          h={`calc(100vh - ${headerSpace}px)`}
          position="fixed"
          zIndex={12}
          p="3"
          {...(isCollapsed ? collapsedProps : expandedProps)}
        >
          <Flex
            id="collapsable-sidebar-top-container"
            direction="column"
            alignItems={isCollapsed ? 'center' : 'left'}
          >
            {isCollapsed ? (
              <ExpandAndCollapseButton
                type="expand"
                onClick={() => {
                  setIsCollapsed((prev) => {
                    sessionStorage.setItem('isCollapsed', String(!prev));
                    return !prev;
                  });
                }}
              />
            ) : (
              <Flex justifyContent="flex-end">
                <ExpandAndCollapseButton
                  type="collapse"
                  onClick={() => {
                    setIsCollapsed((prev) => {
                      sessionStorage.setItem('isCollapsed', String(!prev));
                      return !prev;
                    });
                  }}
                />
              </Flex>
            )}

            <Divider my="5" {...dividerStyleProps} />

            <Box
              id="side-bar-items-container"
              h={`calc(80vh - ${headerSpace}px)`}
              overflowY="auto"
              sx={{ '::-webkit-scrollbar': { display: 'none' } }}
            >
              {(() => {
                if (isCollapsed) {
                  return iterateOverChildren(children);
                }

                return children;
              })()}
            </Box>
          </Flex>

          <Tooltip
            label="Log Out"
            placement="right"
            gutter={12}
            borderRadius="4px"
            px="2"
            py="1"
            bgColor={toolTipBgColor}
            color={toolTipColor}
            isDisabled={!isCollapsed}
          >
            <Flex
              id="log-out-button-container"
              direction="column"
              alignItems={isCollapsed ? 'center' : 'left'}
              mb="7"
              _hover={{ bg: selectedColor, color: 'black' }}
              borderRadius="4px"
              {...(isCollapsed ? { py: '2', mx: '1' } : {})}
              onClick={() => logout()}
              cursor="pointer"
            >
              <LogOutButton iconOnly={isCollapsed} disableOnClick />
            </Flex>
          </Tooltip>
        </Flex>

        <Box pr={isCollapsed ? '75px' : '272px'} h="100%" />
      </Show>
    </>
  );
};
